import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Bell, BellRing,
  User, Trophy, X, Clock, UserCheck, Info, AtSign,
  UserPlus, Briefcase, Calendar,
} from 'lucide-react'
import { useNotifications, useUnreadCount, useMarkNotificationRead, useMarkAllNotificationsRead } from '@/hooks/useNotifications'
import { useSocket } from '@/hooks/useSocket'
import { cn } from '@/utils/cn'
import type { Notification, NotificationType } from '@/types'
import { toastService } from '@/hooks/useToast'

interface NotificationBellProps {
  className?: string
}

const NOTIF_ICON_MAP: Record<NotificationType, React.ElementType> = {
  lead_assigned: User,
  lead_created: UserPlus,
  deal_won: Trophy,
  deal_lost: X,
  deal_created: Briefcase,
  deal_stage_changed: BellRing,
  task_due: Clock,
  task_overdue: Clock,
  task_assigned: UserCheck,
  mention: AtSign,
  workflow_transition: BellRing,
  assignment_changed: UserCheck,
  system: Info,
  comment_added: Bell,
  note_added: Bell,
  meeting_scheduled: Bell,
  calendar_event_created: Calendar,
}

const NOTIF_COLOR_MAP: Record<NotificationType, { bg: string; icon: string }> = {
  lead_assigned: { bg: 'bg-blue-100', icon: 'text-blue-600' },
  lead_created: { bg: 'bg-blue-100', icon: 'text-blue-600' },
  deal_won: { bg: 'bg-green-100', icon: 'text-green-600' },
  deal_lost: { bg: 'bg-red-100', icon: 'text-red-600' },
  deal_created: { bg: 'bg-emerald-100', icon: 'text-emerald-600' },
  deal_stage_changed: { bg: 'bg-purple-100', icon: 'text-purple-600' },
  task_due: { bg: 'bg-amber-100', icon: 'text-amber-600' },
  task_overdue: { bg: 'bg-red-100', icon: 'text-red-600' },
  task_assigned: { bg: 'bg-teal-100', icon: 'text-teal-600' },
  mention: { bg: 'bg-purple-100', icon: 'text-purple-600' },
  workflow_transition: { bg: 'bg-indigo-100', icon: 'text-indigo-600' },
  assignment_changed: { bg: 'bg-teal-100', icon: 'text-teal-600' },
  system: { bg: 'bg-gray-100', icon: 'text-gray-600' },
  comment_added: { bg: 'bg-blue-100', icon: 'text-blue-600' },
  note_added: { bg: 'bg-gray-100', icon: 'text-gray-600' },
  meeting_scheduled: { bg: 'bg-purple-100', icon: 'text-purple-600' },
  calendar_event_created: { bg: 'bg-violet-100', icon: 'text-violet-600' },
}

function formatRelativeTime(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

// Single notification row in dropdown
function NotifRow({ notif, onRead }: { notif: Notification; onRead: (id: string, link?: string | null) => void }) {
  const type = notif.type
  const IconComponent = NOTIF_ICON_MAP[type] || Bell
  const colors = NOTIF_COLOR_MAP[type] || { bg: 'bg-gray-100', icon: 'text-gray-600' }

  return (
    <button
      className={cn(
        'w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-cloud dark:hover:bg-slate-700/60 transition-colors',
        !notif.isRead && 'bg-accent-50/30 dark:bg-slate-700/20'
      )}
      onClick={() => onRead(notif.id, notif.link)}
    >
      {/* Icon */}
      <div className={cn('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center', colors.bg)}>
        <IconComponent className={cn('w-3.5 h-3.5', colors.icon)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-start justify-between gap-2">
          <p className={cn('text-xs leading-tight truncate', !notif.isRead ? 'font-semibold text-navy dark:text-slate-100' : 'font-medium text-steel dark:text-slate-400')}>
            {notif.title}
          </p>
          {!notif.isRead && (
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 mt-1" />
          )}
        </div>
        <p className="text-[10px] text-steel dark:text-slate-400 mt-0.5">
          {formatRelativeTime(notif.createdAt)}
        </p>
      </div>
    </button>
  )
}

export function NotificationBell({ className }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const [bellAnimating, setBellAnimating] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()
  const { count } = useUnreadCount()
  const { notifications, isLoading } = useNotifications({ size: 5 })
  const { markRead } = useMarkNotificationRead()
  const { markAllRead, isLoading: isMarkingAll } = useMarkAllNotificationsRead()
  const { on, off } = useSocket()

  // Bell animation + toast popup on new notification
  const handleNewNotification = useCallback((payload?: any) => {
    setBellAnimating(true)
    setTimeout(() => setBellAnimating(false), 2000)

    // Show a visible toast popup so the user sees the notification immediately
    if (payload?.title) {
      const msg = payload.message || ''
      toastService.info(
        `🔔 ${payload.title}${msg ? `\n${msg}` : ''}`,
        6000  // 6 seconds — long enough to read
      )
    }
  }, [])

  useEffect(() => {
    on('notification:new', handleNewNotification)
    return () => off('notification:new', handleNewNotification)
  }, [on, off, handleNewNotification])

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function handleMarkRead(id: string, link?: string | null) {
    try {
      await markRead(id)
    } catch {
      // ignore
    }
    // Navigate to the notification link and close dropdown
    if (link) {
      setOpen(false)
      navigate(link)
    }
  }

  async function handleMarkAllRead() {
    try {
      await markAllRead()
    } catch {
      // ignore
    }
  }

  const BellIcon = bellAnimating || count > 0 ? BellRing : Bell

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'relative w-8 h-8 flex items-center justify-center rounded-md transition-colors',
          'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200',
          'hover:bg-gray-100 dark:hover:bg-slate-700',
          bellAnimating && 'animate-wiggle'
        )}
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
      >
        <BellIcon size={16} />
        {count > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] rounded-full text-[9px] font-bold text-white flex items-center justify-center leading-none px-0.5"
            style={{ backgroundColor: '#E11D48' }}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white dark:bg-slate-800 border border-mist dark:border-slate-700 rounded-xl shadow-xl z-50 animate-fade-in overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-mist dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-navy dark:text-slate-100">
                Notifications
              </span>
              {count > 0 && (
                <span
                  className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--accent)' }}
                >
                  {count}
                </span>
              )}
            </div>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={isMarkingAll}
                className="text-[10px] font-medium transition-colors hover:opacity-70 disabled:opacity-50"
                style={{ color: 'var(--accent)' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-72 overflow-y-auto divide-y divide-mist dark:divide-slate-700/60">
            {isLoading ? (
              <div className="py-8 text-center">
                <p className="text-xs text-steel dark:text-slate-400">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 flex flex-col items-center text-center px-4">
                <Bell className="w-8 h-8 text-gray-300 dark:text-slate-600 mb-2" />
                <p className="text-xs font-medium text-navy dark:text-slate-300">All caught up!</p>
                <p className="text-[10px] text-steel dark:text-slate-400 mt-0.5">
                  No new notifications right now.
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <NotifRow key={notif.id} notif={notif} onRead={handleMarkRead} />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-mist dark:border-slate-700">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-medium transition-colors hover:opacity-70"
              style={{ color: 'var(--accent)' }}
            >
              View all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
